import React, { useState, useEffect } from 'react';
import { Wrench, AlertTriangle, Save, RefreshCw, CheckCircle2, XCircle, CreditCard, Key, TestTube, Zap, Mail, Chrome } from 'lucide-react';
import { storage } from '../services/storage';
import { useModal } from './ui/ModalProvider';
import { StripeConfig } from '../types';
import { getStripeConfig, testStripeConnection, getSubscriptionItems, getStripePrices } from '../services/stripeService';

export const SettingsPanel: React.FC = () => {
  const { toast, confirm } = useModal();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  
  // Stripe config state
  const [stripeConfig, setStripeConfig] = useState<StripeConfig>({
    apiKeyTest: '',
    apiKeyProd: '',
    environment: 'test',
    subscriptionId: '',
    priceIdNational: '',
    priceIdLocal: '',
  });
  const [stripeSaving, setStripeSaving] = useState(false);
  const [stripeTesting, setStripeTesting] = useState(false);
  const [subscriptionItems, setSubscriptionItems] = useState<Array<{ priceId: string; quantity: number; type: 'national' | 'local' | 'unknown' }>>([]);
  const [noreplyEmail, setNoreplyEmail] = useState('noreply@placid.babynlabs.com');
  const [noreplySaving, setNoreplySaving] = useState(false);
  const [emailDomain, setEmailDomain] = useState('@agences-placid.com');
  const [emailDomainSaving, setEmailDomainSaving] = useState(false);
  const [appUrl, setAppUrl] = useState('https://manager.agences-placid.com');
  const [appUrlSaving, setAppUrlSaving] = useState(false);
  const [stripePrices, setStripePrices] = useState<{
    national?: { id: string; amount: number; currency: string; recurring?: { interval: string; intervalCount: number } | null; error?: string };
    local?: { id: string; amount: number; currency: string; recurring?: { interval: string; intervalCount: number } | null; error?: string };
  }>({});
  const [financeEnabled, setFinanceEnabled] = useState(false);
  const [financeSaving, setFinanceSaving] = useState(false);
  const [billingEnabled, setBillingEnabled] = useState(false);
  const [billingBeta, setBillingBeta] = useState(false);
  const [billingBetaAgencyIds, setBillingBetaAgencyIds] = useState<string[]>([]);
  const [billingSaving, setBillingSaving] = useState(false);
  const [allAgencies, setAllAgencies] = useState<Array<{ id: string; name: string; city: string }>>([]);
  const [yousignEnabled, setYousignEnabled] = useState(false);
  const [yousignSaving, setYousignSaving] = useState(false);
  const [metaEnabled, setMetaEnabled] = useState(false);
  const [metaSaving, setMetaSaving] = useState(false);
  const [extensionId, setExtensionId] = useState('');
  const [extensionIdSaving, setExtensionIdSaving] = useState(false);
  const [extensionStatus, setExtensionStatus] = useState<'checking' | 'connected' | 'disconnected'>('checking');
  const [marketingNotificationRecipients, setMarketingNotificationRecipients] = useState<string[]>([]);
  const [marketingNotificationSaving, setMarketingNotificationSaving] = useState(false);
  const [availableAdmins, setAvailableAdmins] = useState<Array<{ id: string; email: string; firstName: string; lastName: string; role: string }>>([]);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const config = await storage.getSystemConfig('maintenance_mode');
      if (config) {
        setMaintenanceEnabled(config.enabled === true);
        setMaintenanceMessage(config.message || "L'application est actuellement en maintenance. Veuillez réessayer plus tard.");
      }
      
      // Charger la config Stripe
      const stripeConfigData = await getStripeConfig();
      if (stripeConfigData) {
        setStripeConfig(stripeConfigData);
        // Charger les items de l'abonnement si un subscriptionId existe
        if (stripeConfigData.subscriptionId) {
          const { items } = await getSubscriptionItems();
          setSubscriptionItems(items);
        }
        // Charger les prix depuis Stripe
        if (stripeConfigData.priceIdNational || stripeConfigData.priceIdLocal) {
          const { prices } = await getStripePrices();
          setStripePrices(prices);
        }
      }
      
      // Charger la config email noreply
      const emailConfig = await storage.getSystemConfig('email_noreply');
      if (emailConfig) {
        setNoreplyEmail(emailConfig.address || 'noreply@placid.babynlabs.com');
      }
      
      // Charger la config domaine email
      const emailDomainConfig = await storage.getSystemConfig('email_domain');
      if (emailDomainConfig) {
        setEmailDomain(emailDomainConfig.domain || '@agences-placid.com');
      }
      
      // Charger la config URL de l'application
      const appUrlConfig = await storage.getSystemConfig('app_url');
      if (appUrlConfig) {
        setAppUrl(appUrlConfig.url || 'https://manager.agences-placid.com');
      }
      
      // Charger le feature flag finance
      const financeConfig = await storage.getSystemConfig('finance_enabled');
      if (financeConfig) {
        setFinanceEnabled(financeConfig.enabled === true);
      }
      
      // Charger le feature flag facturation (+ mode bêta par agences)
      const billingConfig = await storage.getSystemConfig('billing_enabled');
      if (billingConfig) {
        setBillingEnabled(billingConfig.enabled === true);
        setBillingBeta(billingConfig.beta === true);
        setBillingBetaAgencyIds(Array.isArray(billingConfig.betaAgencyIds) ? billingConfig.betaAgencyIds : []);
      }

      // Liste des agences pour la sélection bêta
      const agencies = await storage.getAllAgencies();
      setAllAgencies(agencies.map(a => ({ id: a.id, name: a.name, city: a.city })));

      // Charger le feature flag yousign
      const yousignConfig = await storage.getSystemConfig('yousign_enabled');
      if (yousignConfig) {
        setYousignEnabled(yousignConfig.enabled === true);
      }
      
      // Charger le feature flag meta
      const metaConfig = await storage.getSystemConfig('meta_enabled');
      if (metaConfig) {
        setMetaEnabled(metaConfig.enabled === true);
      }
      
      // Charger l'ID de l'extension Chrome
      const storedExtensionId = localStorage.getItem('placid_extension_id');
      if (storedExtensionId) {
        setExtensionId(storedExtensionId);
        // Vérifier si l'extension est disponible
        checkExtensionStatus(storedExtensionId);
      } else {
        setExtensionStatus('disconnected');
      }
      
      // Charger la liste des admins/superadmins pour les notifications marketing
      const allProfiles = await storage.getAllProfiles();
      const admins = allProfiles
        .filter(profile => (profile.role === 'admin' || profile.role === 'superadmin') && profile.email)
        .map(profile => ({
          id: profile.id,
          email: profile.email || '',
          firstName: profile.firstName || '',
          lastName: profile.lastName || '',
          role: profile.role
        }));
      setAvailableAdmins(admins);
      
      // Charger la config des destinataires des notifications marketing
      const marketingRecipientsConfig = await storage.getSystemConfig('marketing_notifications_recipients');
      if (marketingRecipientsConfig && marketingRecipientsConfig.userIds) {
        setMarketingNotificationRecipients(marketingRecipientsConfig.userIds || []);
      }
    } catch (error: any) {
      toast.error('Erreur lors du chargement des paramètres: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const confirmed = await confirm({
      title: maintenanceEnabled ? 'Activer le mode maintenance ?' : 'Désactiver le mode maintenance ?',
      message: maintenanceEnabled 
        ? 'Tous les utilisateurs (sauf superadmin) verront la page de maintenance. Continuer ?'
        : 'Les utilisateurs pourront à nouveau accéder à l\'application. Continuer ?',
      confirmText: 'Enregistrer',
      cancelText: 'Annuler'
    });

    if (!confirmed) return;

    setSaving(true);
    try {
      const { error } = await storage.updateSystemConfig('maintenance_mode', {
        enabled: maintenanceEnabled,
        message: maintenanceMessage || "L'application est actuellement en maintenance. Veuillez réessayer plus tard."
      });

      if (error) {
        toast.error('Erreur lors de la sauvegarde: ' + (error.message || 'Erreur inconnue'));
        return;
      }

      toast.success('Paramètres sauvegardés avec succès');
      // Recharger les paramètres pour confirmer
      await loadSettings();
    } catch (error: any) {
      toast.error('Erreur lors de la sauvegarde: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setSaving(false);
    }
  };

  const handleStripeSave = async () => {
    const confirmed = await confirm({
      title: 'Enregistrer la configuration Stripe ?',
      message: 'Les modifications seront appliquées immédiatement. Continuer ?',
      confirmText: 'Enregistrer',
      cancelText: 'Annuler'
    });

    if (!confirmed) return;

    setStripeSaving(true);
    try {
      const { error } = await storage.updateSystemConfig('stripe_config', stripeConfig);

      if (error) {
        toast.error('Erreur lors de la sauvegarde: ' + (error.message || 'Erreur inconnue'));
        return;
      }

      toast.success('Configuration Stripe sauvegardée avec succès');
      await loadSettings();
      // Recharger les prix après sauvegarde
      if (stripeConfig.priceIdNational || stripeConfig.priceIdLocal) {
        const { prices } = await getStripePrices();
        setStripePrices(prices);
      }
    } catch (error: any) {
      toast.error('Erreur lors de la sauvegarde: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setStripeSaving(false);
    }
  };

  const handleTestStripe = async () => {
    setStripeTesting(true);
    try {
      const { success, error } = await testStripeConnection();
      if (success) {
        toast.success('Connexion Stripe réussie !');
      } else {
        toast.error('Erreur de connexion Stripe: ' + (error || 'Erreur inconnue'));
      }
    } catch (error: any) {
      toast.error('Erreur lors du test: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setStripeTesting(false);
    }
  };

  const handleFinanceSave = async () => {
    const confirmed = await confirm({
      title: financeEnabled ? 'Désactiver la gestion des finances ?' : 'Activer la gestion des finances ?',
      message: financeEnabled 
        ? 'Toutes les fonctionnalités de gestion des finances seront désactivées. Continuer ?'
        : 'Les fonctionnalités de gestion des finances seront activées. Continuer ?',
      confirmText: 'Enregistrer',
      cancelText: 'Annuler'
    });

    if (!confirmed) return;

    setFinanceSaving(true);
    try {
      const { error } = await storage.updateSystemConfig('finance_enabled', {
        enabled: financeEnabled
      });

      if (error) {
        toast.error('Erreur lors de la sauvegarde: ' + (error.message || 'Erreur inconnue'));
        return;
      }

      toast.success('Paramètres sauvegardés avec succès');
      await loadSettings();
    } catch (error: any) {
      toast.error('Erreur lors de la sauvegarde: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setFinanceSaving(false);
    }
  };

  const handleBillingSave = async () => {
    const confirmed = await confirm({
      title: billingEnabled ? 'Activer la facturation propriétaires ?' : 'Désactiver la facturation propriétaires ?',
      message: billingEnabled
        ? 'Le menu Facturation (CRG) sera disponible pour les agences. Continuer ?'
        : 'Le menu Facturation (CRG) sera masqué. Continuer ?',
      confirmText: 'Enregistrer',
      cancelText: 'Annuler'
    });

    if (!confirmed) return;

    setBillingSaving(true);
    try {
      const { error } = await storage.updateSystemConfig('billing_enabled', {
        enabled: billingEnabled,
        beta: billingBeta,
        betaAgencyIds: billingBetaAgencyIds
      });

      if (error) {
        toast.error('Erreur lors de la sauvegarde: ' + (error.message || 'Erreur inconnue'));
        return;
      }

      toast.success('Paramètres sauvegardés avec succès');
      await loadSettings();
    } catch (error: any) {
      toast.error('Erreur lors de la sauvegarde: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setBillingSaving(false);
    }
  };

  const checkExtensionStatus = async (id: string) => {
    if (!id || !id.trim()) {
      setExtensionStatus('disconnected');
      return;
    }

    setExtensionStatus('checking');
    
    // Vérifier si chrome.runtime est disponible
    if (typeof window !== 'undefined' && (window as any).chrome && (window as any).chrome.runtime && (window as any).chrome.runtime.sendMessage) {
      try {
        (window as any).chrome.runtime.sendMessage(
          id.trim(),
          { action: 'ping' },
          (response: any) => {
            if ((window as any).chrome.runtime.lastError) {
              setExtensionStatus('disconnected');
            } else {
              setExtensionStatus('connected');
            }
          }
        );
        
        // Timeout après 1 seconde
        setTimeout(() => {
          if (extensionStatus === 'checking') {
            setExtensionStatus('disconnected');
          }
        }, 1000);
      } catch (e) {
        setExtensionStatus('disconnected');
      }
    } else {
      setExtensionStatus('disconnected');
    }
  };

  const handleExtensionIdSave = async () => {
    setExtensionIdSaving(true);
    try {
      const trimmedId = extensionId.trim();
      if (trimmedId) {
        localStorage.setItem('placid_extension_id', trimmedId);
        await checkExtensionStatus(trimmedId);
        toast.success('ID de l\'extension sauvegardé avec succès');
      } else {
        localStorage.removeItem('placid_extension_id');
        setExtensionStatus('disconnected');
        toast.success('ID de l\'extension supprimé');
      }
    } catch (error: any) {
      toast.error('Erreur lors de la sauvegarde: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setExtensionIdSaving(false);
    }
  };

  const handleMarketingNotificationRecipientsSave = async () => {
    setMarketingNotificationSaving(true);
    try {
      const { error } = await storage.updateSystemConfig('marketing_notifications_recipients', {
        userIds: marketingNotificationRecipients
      });

      if (error) {
        toast.error('Erreur lors de la sauvegarde: ' + (error.message || 'Erreur inconnue'));
        return;
      }

      toast.success('Destinataires des notifications marketing sauvegardés avec succès');
      await loadSettings();
    } catch (error: any) {
      toast.error('Erreur lors de la sauvegarde: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setMarketingNotificationSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        <p className="text-slate-500 mt-4">Chargement des paramètres...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Mode Maintenance */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <Wrench className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Mode Maintenance</h2>
              <p className="text-sm text-slate-500 mt-1">
                Activez le mode maintenance pour bloquer l'accès à l'application (sauf pour les superadmin)
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {maintenanceEnabled ? (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold">
                <AlertTriangle className="w-3 h-3" />
                ACTIF
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">
                <CheckCircle2 className="w-3 h-3" />
                INACTIF
              </span>
            )}
          </div>
        </div>

        <div className="space-y-4">
          {/* Toggle Activation */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <label className="text-sm font-semibold text-slate-900">
                Activer le mode maintenance
              </label>
              <p className="text-xs text-slate-500 mt-1">
                Les utilisateurs verront la page de maintenance (sauf superadmin)
              </p>
            </div>
            <button
              onClick={() => setMaintenanceEnabled(!maintenanceEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                maintenanceEnabled ? 'bg-red-600' : 'bg-slate-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  maintenanceEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Message de maintenance */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Message de maintenance
            </label>
            <textarea
              value={maintenanceMessage}
              onChange={(e) => setMaintenanceMessage(e.target.value)}
              placeholder="L'application est actuellement en maintenance. Veuillez réessayer plus tard."
              className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none resize-none"
              rows={4}
            />
            <p className="text-xs text-slate-500 mt-1">
              Ce message sera affiché aux utilisateurs sur la page de maintenance
            </p>
          </div>

          {/* Bouton de sauvegarde */}
          <div className="flex justify-end pt-4 border-t border-slate-200">
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-2.5 rounded-lg font-bold shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Enregistrement...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Enregistrer</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Feature Flag - Gestion des Finances */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Zap className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Gestion des Finances</h2>
              <p className="text-sm text-slate-500 mt-1">
                Activez ou désactivez la gestion des finances (dépenses et revenus)
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <label className="text-sm font-semibold text-slate-900">
                Activer la gestion des finances
              </label>
              <p className="text-xs text-slate-500 mt-1">
                Permet l'accès aux fonctionnalités de gestion des dépenses et revenus
              </p>
            </div>
            <button
              onClick={() => setFinanceEnabled(!financeEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                financeEnabled ? 'bg-blue-600' : 'bg-slate-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  financeEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Bouton de sauvegarde */}
          <div className="flex justify-end pt-4 border-t border-slate-200">
            <button
              onClick={handleFinanceSave}
              disabled={financeSaving}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2.5 rounded-lg font-bold shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {financeSaving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Enregistrement...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Enregistrer</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Feature Flag - Facturation Propriétaires (CRG) */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <Zap className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Facturation Propriétaires (CRG)</h2>
              <p className="text-sm text-slate-500 mt-1">
                Activez ou désactivez la génération des comptes rendus de gestion via RentalReady
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <label className="text-sm font-semibold text-slate-900">
                Activer la facturation propriétaires
              </label>
              <p className="text-xs text-slate-500 mt-1">
                Permet l'accès au menu Facturation et à la génération des CRG mensuels
              </p>
            </div>
            <button
              onClick={() => setBillingEnabled(!billingEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                billingEnabled ? 'bg-emerald-600' : 'bg-slate-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  billingEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Mode bêta */}
          {billingEnabled && (
            <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div>
                <label className="text-sm font-semibold text-slate-900">
                  Mode bêta (agences sélectionnées uniquement)
                </label>
                <p className="text-xs text-slate-500 mt-1">
                  Le menu Facturation n'apparaît que pour les administrateurs et les utilisateurs des agences cochées ci-dessous
                </p>
              </div>
              <button
                onClick={() => setBillingBeta(!billingBeta)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  billingBeta ? 'bg-amber-500' : 'bg-slate-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    billingBeta ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          )}

          {/* Agences bêta */}
          {billingEnabled && billingBeta && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Agences autorisées en bêta
              </label>
              <div className="border border-slate-300 rounded-lg p-4 max-h-64 overflow-y-auto bg-slate-50">
                {allAgencies.length === 0 ? (
                  <p className="text-sm text-slate-500 italic">Aucune agence disponible</p>
                ) : (
                  <div className="space-y-2">
                    {allAgencies.map(agency => (
                      <label
                        key={agency.id}
                        className="flex items-center gap-3 p-2 hover:bg-white rounded cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={billingBetaAgencyIds.includes(agency.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setBillingBetaAgencyIds([...billingBetaAgencyIds, agency.id]);
                            } else {
                              setBillingBetaAgencyIds(billingBetaAgencyIds.filter(id => id !== agency.id));
                            }
                          }}
                          className="w-4 h-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500"
                        />
                        <span className="text-sm font-medium text-slate-900">{agency.name}</span>
                        <span className="text-xs text-slate-500">{agency.city}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {billingBetaAgencyIds.length === 0 && (
                <p className="text-xs text-amber-600 mt-2">
                  Aucune agence cochée : seuls les administrateurs verront le menu Facturation
                </p>
              )}
            </div>
          )}

          {/* Bouton de sauvegarde */}
          <div className="flex justify-end pt-4 border-t border-slate-200">
            <button
              onClick={handleBillingSave}
              disabled={billingSaving}
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-2.5 rounded-lg font-bold shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {billingSaving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Enregistrement...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Enregistrer</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Configuration Stripe */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <CreditCard className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Configuration Stripe</h2>
              <p className="text-sm text-slate-500 mt-1">
                Configurez les clés API Stripe et les Price IDs pour la facturation automatisée
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {stripeConfig.subscriptionId ? (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">
                <CheckCircle2 className="w-3 h-3" />
                CONFIGURÉ
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-bold">
                <XCircle className="w-3 h-3" />
                NON CONFIGURÉ
              </span>
            )}
          </div>
        </div>

        <div className="space-y-4">
          {/* Environnement */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Environnement
            </label>
            <select
              value={stripeConfig.environment}
              onChange={(e) => setStripeConfig({ ...stripeConfig, environment: e.target.value as 'test' | 'production' })}
              className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white"
            >
              <option value="test">Test (sk_test_...)</option>
              <option value="production">Production (sk_live_...)</option>
            </select>
          </div>

          {/* Clés API */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                <TestTube className="w-4 h-4" />
                Clé API Test
              </label>
              <input
                type="password"
                value={stripeConfig.apiKeyTest || ''}
                onChange={(e) => setStripeConfig({ ...stripeConfig, apiKeyTest: e.target.value })}
                placeholder="sk_test_..."
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white font-mono text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                <Zap className="w-4 h-4" />
                Clé API Production
              </label>
              <input
                type="password"
                value={stripeConfig.apiKeyProd || ''}
                onChange={(e) => setStripeConfig({ ...stripeConfig, apiKeyProd: e.target.value })}
                placeholder="sk_live_..."
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white font-mono text-sm"
              />
            </div>
          </div>

          {/* Price IDs */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Price ID National
              </label>
              <input
                type="text"
                value={stripeConfig.priceIdNational || ''}
                onChange={(e) => {
                  setStripeConfig({ ...stripeConfig, priceIdNational: e.target.value });
                  // Recharger le prix si un Price ID valide est entré
                  if (e.target.value && e.target.value.startsWith('price_')) {
                    setTimeout(async () => {
                      const { prices } = await getStripePrices();
                      setStripePrices(prices);
                    }, 500);
                  }
                }}
                placeholder="price_..."
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white font-mono text-sm"
              />
              {stripePrices.national && !stripePrices.national.error && (
                <p className="text-xs text-slate-600 mt-1">
                  <span className="font-semibold">{stripePrices.national.amount} {stripePrices.national.currency}</span>
                  {stripePrices.national.recurring && (
                    <span className="text-slate-500"> / {stripePrices.national.recurring.interval}</span>
                  )}
                </p>
              )}
              {stripePrices.national?.error && (
                <p className="text-xs text-red-500 mt-1">Erreur: {stripePrices.national.error}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Price ID Local
              </label>
              <input
                type="text"
                value={stripeConfig.priceIdLocal || ''}
                onChange={(e) => {
                  setStripeConfig({ ...stripeConfig, priceIdLocal: e.target.value });
                  // Recharger le prix si un Price ID valide est entré
                  if (e.target.value && e.target.value.startsWith('price_')) {
                    setTimeout(async () => {
                      const { prices } = await getStripePrices();
                      setStripePrices(prices);
                    }, 500);
                  }
                }}
                placeholder="price_..."
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white font-mono text-sm"
              />
              {stripePrices.local && !stripePrices.local.error && (
                <p className="text-xs text-slate-600 mt-1">
                  <span className="font-semibold">{stripePrices.local.amount} {stripePrices.local.currency}</span>
                  {stripePrices.local.recurring && (
                    <span className="text-slate-500"> / {stripePrices.local.recurring.interval}</span>
                  )}
                </p>
              )}
              {stripePrices.local?.error && (
                <p className="text-xs text-red-500 mt-1">Erreur: {stripePrices.local.error}</p>
              )}
            </div>
          </div>

          {/* Subscription ID */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              ID d'abonnement Stripe
            </label>
            <input
              type="text"
              value={stripeConfig.subscriptionId || ''}
              onChange={(e) => setStripeConfig({ ...stripeConfig, subscriptionId: e.target.value })}
              placeholder="sub_..."
              className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white font-mono text-sm"
            />
            <p className="text-xs text-slate-500 mt-1">
              ID de l'abonnement Stripe pour cette instance. Créez un abonnement dans Stripe Dashboard et entrez l'ID ici.
            </p>
          </div>

          {/* Subscription Items Info */}
          {stripeConfig.subscriptionId && subscriptionItems.length > 0 && (
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <p className="text-sm font-semibold text-slate-700 mb-2">État actuel de l'abonnement :</p>
              <div className="space-y-1 mb-3">
                {subscriptionItems.map((item, idx) => {
                  let priceDisplay = '';
                  let totalAmount = 0;
                  let currency = '';
                  
                  if (item.type === 'national' && stripePrices.national && !stripePrices.national.error) {
                    priceDisplay = `${stripePrices.national.amount} ${stripePrices.national.currency}`;
                    totalAmount = item.quantity * stripePrices.national.amount;
                    currency = stripePrices.national.currency;
                  } else if (item.type === 'local' && stripePrices.local && !stripePrices.local.error) {
                    priceDisplay = `${stripePrices.local.amount} ${stripePrices.local.currency}`;
                    totalAmount = item.quantity * stripePrices.local.amount;
                    currency = stripePrices.local.currency;
                  }
                  
                  return (
                    <div key={idx} className="text-xs text-slate-600 flex justify-between items-center">
                      <span>
                        {item.type === 'national' && (
                          <>Utilisateurs nationaux : {item.quantity} × {priceDisplay || 'Prix non disponible'}/mois</>
                        )}
                        {item.type === 'local' && (
                          <>Utilisateurs locaux : {item.quantity} × {priceDisplay || 'Prix non disponible'}/mois</>
                        )}
                        {item.type === 'unknown' && (
                          <>Autre ({item.priceId}) : {item.quantity}</>
                        )}
                      </span>
                      {totalAmount > 0 && (
                        <span className="font-semibold text-slate-800">
                          = {totalAmount.toFixed(2)} {currency}/mois
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Total général */}
              {(() => {
                let grandTotal = 0;
                let grandTotalCurrency = '';
                
                subscriptionItems.forEach(item => {
                  if (item.type === 'national' && stripePrices.national && !stripePrices.national.error) {
                    grandTotal += item.quantity * stripePrices.national.amount;
                    grandTotalCurrency = stripePrices.national.currency;
                  } else if (item.type === 'local' && stripePrices.local && !stripePrices.local.error) {
                    grandTotal += item.quantity * stripePrices.local.amount;
                    if (!grandTotalCurrency) grandTotalCurrency = stripePrices.local.currency;
                  }
                });
                
                return grandTotal > 0 ? (
                  <div className="pt-3 border-t border-slate-300 flex justify-between items-center">
                    <span className="text-sm font-bold text-slate-900">Total mensuel :</span>
                    <span className="text-sm font-bold text-slate-900">
                      {grandTotal.toFixed(2)} {grandTotalCurrency}/mois
                    </span>
                  </div>
                ) : null;
              })()}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <button
              onClick={handleTestStripe}
              disabled={stripeTesting}
              className="px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg font-semibold shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
            >
              {stripeTesting ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Test...</span>
                </>
              ) : (
                <>
                  <Key className="w-4 h-4" />
                  <span>Tester la connexion</span>
                </>
              )}
            </button>
            <button
              onClick={handleStripeSave}
              disabled={stripeSaving}
              className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-2.5 rounded-lg font-bold shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {stripeSaving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Enregistrement...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Enregistrer</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Feature Flag - Signature Yousign */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Zap className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Signature Yousign</h2>
              <p className="text-sm text-slate-500 mt-1">
                Activez ou désactivez la fonctionnalité de signature électronique via Yousign
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <label className="text-sm font-semibold text-slate-900">
                Activer la signature Yousign
              </label>
              <p className="text-xs text-slate-500 mt-1">
                Permet l'utilisation de la signature électronique via Yousign dans les contrats
              </p>
            </div>
            <button
              onClick={() => setYousignEnabled(!yousignEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                yousignEnabled ? 'bg-green-600' : 'bg-slate-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  yousignEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Bouton de sauvegarde */}
          <div className="flex justify-end pt-4 border-t border-slate-200">
            <button
              onClick={async () => {
                const confirmed = await confirm({
                  title: yousignEnabled ? 'Activer Yousign ?' : 'Désactiver Yousign ?',
                  message: yousignEnabled 
                    ? 'Le bouton Yousign sera disponible dans les contrats. Continuer ?'
                    : 'Le bouton Yousign sera grisé dans les contrats. Continuer ?',
                  confirmText: 'Enregistrer',
                  cancelText: 'Annuler'
                });

                if (!confirmed) return;

                setYousignSaving(true);
                try {
                  const { error } = await storage.updateSystemConfig('yousign_enabled', {
                    enabled: yousignEnabled
                  });

                  if (error) {
                    toast.error('Erreur lors de la sauvegarde: ' + (error.message || 'Erreur inconnue'));
                    return;
                  }

                  toast.success('Paramètres sauvegardés avec succès');
                  await loadSettings();
                } catch (error: any) {
                  toast.error('Erreur lors de la sauvegarde: ' + (error.message || 'Erreur inconnue'));
                } finally {
                  setYousignSaving(false);
                }
              }}
              disabled={yousignSaving}
              className="bg-green-500 hover:bg-green-600 text-white px-6 py-2.5 rounded-lg font-bold shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {yousignSaving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Enregistrement...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Enregistrer</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Feature Flag - Gestion des leads prestataires marketing */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Zap className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Gestion des leads prestataires marketing</h2>
              <p className="text-sm text-slate-500 mt-1">
                Activez ou désactivez la fonctionnalité de gestion des leads prestataires marketing
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <label className="text-sm font-semibold text-slate-900">
                Activer la gestion des leads prestataires marketing
              </label>
              <p className="text-xs text-slate-500 mt-1">
                Permet l'accès aux fonctionnalités d'intégration Meta, stats Meta et gestion des leads prestataires marketing
              </p>
            </div>
            <button
              onClick={() => setMetaEnabled(!metaEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                metaEnabled ? 'bg-blue-600' : 'bg-slate-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  metaEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Bouton de sauvegarde */}
          <div className="flex justify-end pt-4 border-t border-slate-200">
            <button
              onClick={async () => {
                const confirmed = await confirm({
                  title: metaEnabled ? 'Activer la gestion des leads prestataires marketing ?' : 'Désactiver la gestion des leads prestataires marketing ?',
                  message: metaEnabled 
                    ? 'Les menus et fonctionnalités de gestion des leads prestataires marketing seront disponibles. Continuer ?'
                    : 'Les menus et fonctionnalités de gestion des leads prestataires marketing seront masqués. Continuer ?',
                  confirmText: 'Enregistrer',
                  cancelText: 'Annuler'
                });

                if (!confirmed) return;

                setMetaSaving(true);
                try {
                  const { error } = await storage.updateSystemConfig('meta_enabled', {
                    enabled: metaEnabled
                  });

                  if (error) {
                    toast.error('Erreur lors de la sauvegarde: ' + (error.message || 'Erreur inconnue'));
                    return;
                  }

                  toast.success('Paramètres sauvegardés avec succès');
                  await loadSettings();
                } catch (error: any) {
                  toast.error('Erreur lors de la sauvegarde: ' + (error.message || 'Erreur inconnue'));
                } finally {
                  setMetaSaving(false);
                }
              }}
              disabled={metaSaving}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2.5 rounded-lg font-bold shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {metaSaving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Enregistrement...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Enregistrer</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Configuration Email Noreply */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Email Noreply</h2>
              <p className="text-sm text-slate-500 mt-1">
                Adresse email utilisée pour l'envoi d'emails automatiques (notifications Stripe, etc.)
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Adresse email noreply
            </label>
            <input
              type="email"
              value={noreplyEmail}
              onChange={(e) => setNoreplyEmail(e.target.value)}
              placeholder="noreply@placid.babynlabs.com"
              className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white"
            />
            <p className="text-xs text-slate-500 mt-1">
              Cette adresse sera utilisée comme expéditeur pour tous les emails automatiques
            </p>
          </div>

          <div className="flex justify-end pt-4 border-t border-slate-200">
            <button
              onClick={async () => {
                setNoreplySaving(true);
                try {
                  const { error } = await storage.updateSystemConfig('email_noreply', {
                    address: noreplyEmail,
                  });
                  if (error) {
                    toast.error('Erreur lors de la sauvegarde: ' + (error.message || 'Erreur inconnue'));
                  } else {
                    toast.success('Email noreply sauvegardé avec succès');
                    await loadSettings();
                  }
                } catch (error: any) {
                  toast.error('Erreur lors de la sauvegarde: ' + (error.message || 'Erreur inconnue'));
                } finally {
                  setNoreplySaving(false);
                }
              }}
              disabled={noreplySaving}
              className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-2.5 rounded-lg font-bold shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {noreplySaving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Enregistrement...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Enregistrer</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Configuration Domaine Email */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Mail className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Domaine Email Agences</h2>
              <p className="text-sm text-slate-500 mt-1">
                Domaine requis pour les emails des agences (ex: @agences-placid.com)
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Domaine email
            </label>
            <input
              type="text"
              value={emailDomain}
              onChange={(e) => setEmailDomain(e.target.value)}
              placeholder="@agences-placid.com"
              className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white"
            />
            <p className="text-xs text-slate-500 mt-1">
              Tous les emails des agences doivent se terminer par ce domaine. Une erreur sera affichée si un email ne respecte pas ce format.
            </p>
          </div>

          <div className="flex justify-end pt-4 border-t border-slate-200">
            <button
              onClick={async () => {
                setEmailDomainSaving(true);
                try {
                  const { error } = await storage.updateSystemConfig('email_domain', {
                    domain: emailDomain,
                  });
                  if (error) {
                    toast.error('Erreur lors de la sauvegarde: ' + (error.message || 'Erreur inconnue'));
                  } else {
                    toast.success('Domaine email sauvegardé avec succès');
                    await loadSettings();
                  }
                } catch (error: any) {
                  toast.error('Erreur lors de la sauvegarde: ' + (error.message || 'Erreur inconnue'));
                } finally {
                  setEmailDomainSaving(false);
                }
              }}
              disabled={emailDomainSaving}
              className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-2.5 rounded-lg font-bold shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {emailDomainSaving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Enregistrement...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Enregistrer</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Configuration URL Application */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Zap className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">URL de l'Application</h2>
              <p className="text-sm text-slate-500 mt-1">
                URL utilisée pour les redirections OAuth et les liens dans les emails (ex: https://manager.agences-placid.com)
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              URL de l'application
            </label>
            <input
              type="url"
              value={appUrl}
              onChange={(e) => setAppUrl(e.target.value)}
              placeholder="https://manager.agences-placid.com"
              className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white"
            />
            <p className="text-xs text-slate-500 mt-1">
              Cette URL est utilisée par les fonctions Supabase Edge pour les redirections OAuth et autres callbacks.
            </p>
          </div>

          <div className="flex justify-end pt-4 border-t border-slate-200">
            <button
              onClick={async () => {
                setAppUrlSaving(true);
                try {
                  const { error } = await storage.updateSystemConfig('app_url', {
                    url: appUrl,
                  });
                  if (error) {
                    toast.error('Erreur lors de la sauvegarde: ' + (error.message || 'Erreur inconnue'));
                  } else {
                    toast.success('URL de l\'application sauvegardée avec succès');
                    await loadSettings();
                  }
                } catch (error: any) {
                  toast.error('Erreur lors de la sauvegarde: ' + (error.message || 'Erreur inconnue'));
                } finally {
                  setAppUrlSaving(false);
                }
              }}
              disabled={appUrlSaving}
              className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-2.5 rounded-lg font-bold shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {appUrlSaving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Enregistrement...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Enregistrer</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Configuration Extension Chrome */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Chrome className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Extension Chrome Leboncoin</h2>
              <p className="text-sm text-slate-500 mt-1">
                Configurez l'ID de l'extension Chrome pour l'import automatique depuis Leboncoin
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {extensionStatus === 'checking' ? (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-bold">
                <RefreshCw className="w-3 h-3 animate-spin" />
                VÉRIFICATION...
              </span>
            ) : extensionStatus === 'connected' ? (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">
                <CheckCircle2 className="w-3 h-3" />
                CONNECTÉ
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold">
                <XCircle className="w-3 h-3" />
                NON CONNECTÉ
              </span>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              ID de l'extension Chrome
            </label>
            <input
              type="text"
              value={extensionId}
              onChange={(e) => {
                setExtensionId(e.target.value);
                setExtensionStatus('disconnected');
              }}
              placeholder="abcdefghijklmnopqrstuvwxyz123456"
              className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white font-mono text-sm"
            />
            <p className="text-xs text-slate-500 mt-1">
              Pour trouver l'ID de l'extension : 
              <br />
              1. Allez sur <code className="bg-slate-100 px-1 py-0.5 rounded text-xs font-mono">chrome://extensions/</code>
              <br />
              2. Activez le "Mode développeur" (toggle en haut à droite)
              <br />
              3. Trouvez l'extension "Placid Manager - Leboncoin Parser BÊTA"
              <br />
              4. Copiez l'ID affiché sous le nom de l'extension
              <br />
              <br />
              Ou trouvez l'ID dans l'URL de la page Chrome Web Store de l'extension : 
              <br />
              <code className="bg-slate-100 px-1 py-0.5 rounded text-xs font-mono">https://chrome.google.com/webstore/detail/[ID_ICI]/...</code>
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <button
              onClick={() => checkExtensionStatus(extensionId)}
              disabled={!extensionId.trim() || extensionStatus === 'checking'}
              className="px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg font-semibold shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
            >
              {extensionStatus === 'checking' ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Vérification...</span>
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  <span>Vérifier la connexion</span>
                </>
              )}
            </button>
            <button
              onClick={handleExtensionIdSave}
              disabled={extensionIdSaving}
              className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-2.5 rounded-lg font-bold shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {extensionIdSaving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Enregistrement...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Enregistrer</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Configuration Destinataires Notifications Marketing */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Destinataires Notifications Marketing</h2>
              <p className="text-sm text-slate-500 mt-1">
                Choisissez les destinataires qui recevront les notifications par email pour les leads marketing (webhook Meta et événements marketing)
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Sélectionner les destinataires
            </label>
            <p className="text-xs text-slate-500 mb-3">
              Si aucun destinataire n'est sélectionné, tous les admins/superadmins recevront les notifications (comportement par défaut).
            </p>
            <div className="border border-slate-300 rounded-lg p-4 max-h-64 overflow-y-auto bg-slate-50">
              {availableAdmins.length === 0 ? (
                <p className="text-sm text-slate-500 italic">Aucun admin/superadmin disponible</p>
              ) : (
                <div className="space-y-2">
                  {availableAdmins.map((admin) => (
                    <label
                      key={admin.id}
                      className="flex items-center gap-3 p-2 hover:bg-white rounded cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={marketingNotificationRecipients.includes(admin.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setMarketingNotificationRecipients([...marketingNotificationRecipients, admin.id]);
                          } else {
                            setMarketingNotificationRecipients(
                              marketingNotificationRecipients.filter(id => id !== admin.id)
                            );
                          }
                        }}
                        className="w-4 h-4 text-amber-600 border-slate-300 rounded focus:ring-amber-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-900">
                            {admin.firstName} {admin.lastName}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            admin.role === 'superadmin' 
                              ? 'bg-purple-100 text-purple-700' 
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {admin.role === 'superadmin' ? 'Superadmin' : 'Admin'}
                          </span>
                        </div>
                        <span className="text-xs text-slate-500">{admin.email}</span>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            {marketingNotificationRecipients.length > 0 && (
              <p className="text-xs text-slate-600 mt-2">
                {marketingNotificationRecipients.length} destinataire(s) sélectionné(s)
              </p>
            )}
            {marketingNotificationRecipients.length === 0 && (
              <p className="text-xs text-amber-600 mt-2">
                Aucun destinataire sélectionné - tous les admins recevront les notifications par défaut
              </p>
            )}
          </div>

          <div className="flex justify-end pt-4 border-t border-slate-200">
            <button
              onClick={handleMarketingNotificationRecipientsSave}
              disabled={marketingNotificationSaving}
              className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-2.5 rounded-lg font-bold shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {marketingNotificationSaving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Enregistrement...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Enregistrer</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Info sur l'accès caché */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-amber-900 mb-1">Accès d'urgence</p>
            <p className="text-amber-700">
              Pour vous connecter même en mode maintenance, ajoutez <code className="bg-amber-100 px-1.5 py-0.5 rounded text-xs font-mono">?force=maintenance_login</code> à l'URL de connexion.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
